import cloneDeep from 'lodash/cloneDeep';

import { DesignData, Spread } from 'editor/src/store/design/types';
import isPageMediaEmpty from 'editor/src/store/design/util/isPageMediaEmpty';
import { Product, ProductVariation, Variant } from 'editor/src/store/variants/types';

import { getCanonicalArea } from 'editor/src/util/reflectDesignData';
import { ReflectContext } from 'editor/src/util/reflectDesignData/getReflectContext';
import reflectSpreadAndApply from 'editor/src/util/reflectDesignData/reflectSpreadAndApply';

export type SizeOption = { value: string; title: string };

type SizedProduct = Pick<Product, 'sizeControlKey' | 'productControls' | 'sizeSpecificAreas'> | undefined;

export function getVariationSize(variation: ProductVariation, product: SizedProduct): SizeOption | undefined {
  const sizeControlKey = product?.sizeControlKey;
  const value = sizeControlKey ? variation[sizeControlKey] : undefined;
  if (!value) {
    return undefined;
  }
  const control = product?.productControls?.find(({ key }) => key === sizeControlKey);
  return { value, title: control?.options.find((option) => option.value === value)?.title ?? value };
}

export const getSizeSpecificAreas = (product: SizedProduct): string[] => product?.sizeSpecificAreas ?? [];

export const isSizeSpecificSpread = (product: SizedProduct, spread: Spread | undefined) =>
  !!spread && getSizeSpecificAreas(product).includes(getCanonicalArea(spread.name));

const findAreaSpreadIndex = (design: DesignData, area: string) =>
  design.spreads.findIndex((spread) => getCanonicalArea(spread.name) === area);

export const isSpreadEmpty = (spread: Spread) => spread.pages.every(isPageMediaEmpty);

function clearSpreadContent(spread: Spread) {
  spread.pages.forEach((page) => {
    page.groups.media = page.groups.media?.filter((element) => (element as { sample?: boolean }).sample);
  });
  delete spread.conditionGroup;
  delete spread.slotLayouts;
}

/** `design` with the artwork of `area` removed, keeping sample layers. */
export function withoutAreaArtwork(design: DesignData, area: string): DesignData {
  const index = findAreaSpreadIndex(design, area);
  if (index < 0) {
    return design;
  }
  const spreads = [...design.spreads];
  spreads[index] = cloneDeep(spreads[index]);
  clearSpreadContent(spreads[index]);
  return { ...design, spreads };
}

/** Whether `design` prints `area`: a size's variants may differ in print areas, and a design has a spread per area. */
export const printsArea = (design: DesignData | undefined, area: string): design is DesignData =>
  !!design && findAreaSpreadIndex(design, area) >= 0;

/**
 * `design` with its size-specific print areas holding the artwork of `sources`, designs of the same size in order of
 * preference: each area takes it from the first that prints the area. Without one it is emptied, so a size missing
 * its own artwork shows up instead of printing another size's.
 */
export function withSizeSpecificAreasFrom(
  design: DesignData,
  sources: ReadonlyArray<DesignData | undefined>,
  areas: string[],
  context: ReflectContext,
): DesignData {
  let result = design;
  areas.forEach((area) => {
    const index = findAreaSpreadIndex(result, area);
    const source = sources.find((candidate) => printsArea(candidate, area));
    if (index < 0 || source === design) {
      return;
    }

    if (result === design) {
      result = { ...design, spreads: [...design.spreads] };
    }
    const destSpread = cloneDeep(result.spreads[index]);
    const sourceSpread = source?.spreads[findAreaSpreadIndex(source, area)];
    if (sourceSpread) {
      reflectSpreadAndApply({
        sourceSpread,
        destSpread,
        sourceDesign: { ...result, spreads: [sourceSpread], spread_groups: undefined },
        newDesign: result,
        sourceSpreadIndex: 0,
        context,
      });
    } else {
      clearSpreadContent(destSpread);
    }
    result.spreads[index] = destSpread;
  });
  return result;
}

/** Each size's designs among `variants`, for a variant that has no artwork of its own yet. */
export function getDesignsBySize(
  variants: ReadonlyArray<Pick<Variant, 'variation' | 'designData'>>,
  product: SizedProduct,
): Map<string, DesignData[]> {
  const designsBySize = new Map<string, DesignData[]>();
  variants.forEach(({ variation, designData }) => {
    const size = getVariationSize(variation, product)?.value;
    if (size && designData) {
      designsBySize.set(size, [...(designsBySize.get(size) ?? []), designData]);
    }
  });
  return designsBySize;
}
